import React from 'react';
import PropTypes from 'prop-types';
import SvgSymbol from 'common/components/svg-symbol';
import './index.scss';

const preventDefault = (event) => event.preventDefault();

export default class Button extends React.Component {
  static propTypes = {
    children: PropTypes.any, // So it can support arrays and components
    className: PropTypes.string,
    href: PropTypes.string,
    isDepressed: PropTypes.bool,
    isDisabled: PropTypes.bool,
    narrow: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func,
    style: PropTypes.object,
    svgName: PropTypes.string,
    tag: PropTypes.string,
    type: PropTypes.string,
    withIcon: PropTypes.bool,
  };

  static defaultProps = {
    children: '',
    isDepressed: false,
    isDisabled: false,
    narrow: false,
    onClick: () => {},
    onKeyDown: () => {},
    style: {},
    svgName: '',
    tag: 'a',
    type: 'standard',
    withIcon: false,
  };

  constructor(props) {
    const {isDepressed, type, withIcon} = props;

    // Ensure type is supported
    const supportedTypes = [
      'standard',
      'green',
      'gray',
      'blue',
      'red',
      'select',
      'table',
      'table-select',
    ];

    if (supportedTypes.indexOf(type) === -1) {
      throw new Error(
        `Invalid "type" prop passed to Button.  Supported "type" props include ${supportedTypes.join(
          ', ',
        )}`,
      );
    }

    // Ensure that proper icon type is passed with icon
    const withIconSupportedTypes = [
      'standard',
      'green',
      'blue',
      'table',
      'red',
    ];

    if (withIconSupportedTypes.indexOf(type) === -1 && withIcon === true) {
      throw new Error(
        `Invalid combination of "type" and "withIcon" props.  You can only pass withIcon for types: ${withIconSupportedTypes.join(
          ', ',
        )}`,
      );
    }

    // Ensure that isDepressed is only passed with certain types that there are style rules for
    const isDepressedSupportedTypes = ['select', 'table-select', 'green'];

    if (isDepressedSupportedTypes.indexOf(type) === -1 && isDepressed) {
      throw new Error(
        `Invalid combination of "type" and "isDepressed" props. You can only pass isDepressed for types: ${isDepressedSupportedTypes.join(
          ', ',
        )}`,
      );
    }

    super(props);
  }

  render() {
    const buttonClassesByType = {
      standard: 'Button--standard',
      green: 'Button--green',
      gray: 'Button--gray',
      blue: 'Button--blue',
      red: 'Button--red',
      select: 'Button--select',
      table: 'Button--table',
      'table-select': 'Button--table-select',
    };

    const iconsByType = {
      blue: 'arrow-right',
      green: 'plus',
      red: 'delete-white',
      standard: 'edit',
      table: 'edit',
    };

    // Only show dropdown for certain types
    const dropdownTypes = ['select', 'table-select'];
    const dropdownIcon =
      dropdownTypes.indexOf(this.props.type) === -1 ? null : (
        <SvgSymbol
          className={'Button-dropdown'}
          key={'dropdownIcon'}
          name={'dropdown'}
        />
      );

    const svgName = this.props.svgName || iconsByType[this.props.type];
    const icon = !this.props.withIcon ? null : (
      <SvgSymbol className={'Button-icon'} key={'icon'} name={svgName} />
    );

    const buttonBaseClass = '';
    const buttonTypeClass = buttonClassesByType[this.props.type];
    const buttonDisabledClass = this.props.isDisabled ? 'isDisabled' : '';
    const buttonDepressedClass = this.props.isDepressed ? 'isDepressed' : '';
    const buttonWithIconClass = this.props.withIcon ? 'withIcon' : '';
    const buttonNarrowClass = this.props.narrow ? 'Button-isNarrow' : '';

    const buttonClassList = [];

    buttonClassList.push(
      buttonBaseClass,
      buttonTypeClass,
      buttonDisabledClass,
      buttonDepressedClass,
      buttonWithIconClass,
      buttonNarrowClass,
      this.props.className,
    );

    // Clear empty strings out of class list
    const filteredButtonClassList = buttonClassList.filter((x) => x);

    // Concatenate filtered class list to form className
    const buttonClass = filteredButtonClassList.join(' ');

    // Allow arbitrary data attributes passed as props to be placed on button
    const dataProps = {};
    for (const propName of Object.keys(this.props)) {
      if (propName.slice(0, 5) === 'data-') {
        dataProps[propName] = this.props[propName];
      }
    }

    const attributes = {
      ...dataProps,
    };

    if (this.props.href) {
      attributes.href = this.props.href;
    }

    return React.createElement(
      this.props.tag,
      {
        ...attributes,
        className: buttonClass,
        onClick: this.props.isDisabled ? preventDefault : this.props.onClick,
        onKeyDown: this.props.isDisabled
          ? preventDefault
          : this.props.onKeyDown,
        style: this.props.style,
      },
      [icon, this.props.children, dropdownIcon],
    );
  }
}
